import {
  WindowOrderStatus,
  WindowOrderSummary,
} from '@cutr/constants/windows/windows';
import { useTranslation } from 'react-i18next';

import { BadgeVariant } from '@/primitives/Badge';
import Badge from '@/primitives/Badge';
import { dateFormatter } from '@/utils/format';

type StatusBadgeProps =
  | {
      order: WindowOrderSummary;
    }
  | { status: WindowOrderStatus };

const badgeVariantMap: Record<WindowOrderStatus, BadgeVariant> = {
  draft: 'default' as const,
  quote_requested: 'signal' as const,
  quote_ready: 'new' as const,
  change_requested: 'signal' as const,
  order_placed: 'success' as const,
  cancelled: 'alert' as const,
};

export const StatusBadge = (
  props: StatusBadgeProps & { style?: React.CSSProperties }
) => {
  const { t } = useTranslation();

  if ('status' in props) {
    const { status } = props;

    const badgeVariant = badgeVariantMap[status];
    return (
      <Badge variant={badgeVariant as BadgeVariant} {...props}>
        {t(`reseller.orderStatus.${status}`)}
      </Badge>
    );
  }

  const { order } = props;

  const {
    status,
    updatedAt,
    quoteRequestedAt,
    quoteReadyAt,
    changeRequestedAt,
    orderPlacedAt,
    cancelledAt,
  } = order;

  const tooltipMap: Record<WindowOrderStatus, string> = {
    draft: `${t('reseller.orderStatus.timestampLabels.updatedAt', {
      date: dateFormatter(updatedAt),
    })}`,
    quote_requested: `${t(
      'reseller.orderStatus.timestampLabels.quoteRequestedAt',
      {
        date: dateFormatter(quoteRequestedAt),
      }
    )}`,
    quote_ready: `${t('reseller.orderStatus.timestampLabels.quoteReadyAt', {
      date: dateFormatter(quoteReadyAt),
    })}`,
    change_requested: `${t(
      'reseller.orderStatus.timestampLabels.changeRequestedAt',
      {
        date: dateFormatter(changeRequestedAt),
      }
    )}`,
    order_placed: `${t('reseller.orderStatus.timestampLabels.orderPlacedAt', {
      date: dateFormatter(orderPlacedAt),
    })}`,
    cancelled: `${t('reseller.orderStatus.timestampLabels.cancelledAt', {
      date: dateFormatter(cancelledAt),
    })}`,
  };

  const tooltip = tooltipMap[status];
  const badgeVariant = badgeVariantMap[status];
  return (
    <div className={tooltip ? 'tooltip' : ''} data-tooltip={tooltip}>
      <Badge variant={badgeVariant as BadgeVariant} {...props}>
        {t(`reseller.orderStatus.${status}`)}
      </Badge>
    </div>
  );
};
